import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class LotMigratorExportService {
    public static readonly LOT_IMPORT_HEADERS: readonly string[] = [
        'LotNumber',
        'DpNumber',
        'Street',
        'Suburb',
        'City',
        'PostCode',
        'AddressRegion',
        'LotContractType',
        'LotType',
        'LotBuildType',
        'FloorArea',
        'LandArea',
        'PriceLand',
        'PriceSell',
        'JobNumber',
        'NoBedrooms',
        'NoBathrooms',
        'NoLivingRooms',
        'NoGarages',
        'NoCarparks',
        'NoStudy',
        'BuildTypeLink',
        'EstJobStart',
        'EstJobEnd',
        'ConGenFee',
        'LotHoldDep',
        'IsKitset',
        'IsKiwibuild',
        'IsTransportable',
        'IsGrowhome',
        'ExternalLandVendor',
        'LotPurchasePrice',
        'LotDeposit'
    ] as const;

    public buildImportFile(filename: string, lotsFormArray: FormArray): File {
        const headers = LotMigratorExportService.LOT_IMPORT_HEADERS;
        const rows = this.collectExportRows(lotsFormArray);
        const csv = this.buildCsv(headers, rows);

        return new File([csv], filename, { type: 'text/csv;charset=utf-8;' });
    }

    private collectExportRows(lotsFormArray: FormArray): Array<Record<string, unknown>> {
        const headers = LotMigratorExportService.LOT_IMPORT_HEADERS;
        const rows: Array<Record<string, unknown>> = [];

        const pushFromFormGroup = (formGroup: FormGroup): void => {
            const value = formGroup.getRawValue();
            const row: Record<string, unknown> = {};

            headers.forEach(header => {
                row[header] = value[header];
            });

            rows.push(row);
        };

        if (lotsFormArray?.length > 0) {
            (lotsFormArray.controls as FormGroup[]).forEach(pushFromFormGroup);
        }

        return rows;
    }

    private buildCsv(headers: readonly string[], rows: Array<Record<string, unknown>>): string {
        const headerLine = headers.map(header => this.csvEscape(header)).join(',');
        const lines = rows.map(row =>
            headers.map(header => this.csvEscape(this.normalizeCellValue(header, row[header]))).join(',')
        );

        return '\uFEFF' + [headerLine, ...lines].join('\r\n');
    }

    private normalizeCellValue(key: string, rawValue: unknown): string | number {
        if (rawValue == null) {
            return '';
        }

        if (key === 'LotContractType') {
            rawValue = this.toTitleCase(String(rawValue)).replace(/\s+/g, '');
        }

        if (key === 'LotType') {
            rawValue = String(rawValue).replace(/\s+/g, '');
        }

        if (key === 'EstJobStart' || key === 'EstJobEnd') {
            return this.formatDateForImport(rawValue);
        }

        if (
            key === 'IsKitset' ||
            key === 'IsKiwibuild' ||
            key === 'IsTransportable' ||
            key === 'IsGrowhome'
        ) {
            return rawValue ? 1 : 0;
        }

        const numericFields = new Set([
            'FloorArea',
            'LandArea',
            'PriceLand',
            'PriceSell',
            'ConGenFee',
            'LotHoldDep',
            'LotPurchasePrice',
            'LotDeposit',
            'NoBedrooms',
            'NoBathrooms',
            'NoLivingRooms',
            'NoGarages',
            'NoCarparks',
            'NoStudy'
        ]);

        if (numericFields.has(key)) {
            const parsed = typeof rawValue === 'number' ? rawValue : Number(rawValue);
            return Number.isFinite(parsed) ? parsed : '';
        }

        return String(rawValue);
    }

    private formatDateForImport(value: unknown): string {
        if (!value) {
            return '';
        }

        const date = value instanceof Date ? value : new Date(value as string);
        if (Number.isNaN(date.getTime())) {
            return '';
        }

        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    }

    private csvEscape(value: unknown): string {
        if (value == null) {
            return '';
        }

        const stringValue = String(value);
        if (/[",\r\n]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
    }

    private toTitleCase(value: string): string {
        return value
            .toLowerCase()
            .split(' ')
            .map(word => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
            .join(' ');
    }
}
